import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { EventContentArg } from '@fullcalendar/core'
import {
  addDays,
  addMinutes,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  parse,
  parseISO,
  startOfWeek,
  subHours,
  subMinutes,
} from 'date-fns'
import { format, formatInTimeZone } from 'date-fns-tz'
import { isEmpty, isNil } from 'lodash-es'
import styled from 'styled-components/native'

import { toFakeUTCISOString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { DAYS_OF_WEEK_LONG_LABELS } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/messages'
import {
  BuiltEvent,
  DEFAULT_SESSION_TYPE_BY_MODE,
  EventModality,
  getNpSessionDuration,
  getNpSessionTypeFromBookingType,
  NpSessionType,
  SessionTypeSelectorMode,
  toBookingType,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { FlexibleTimeRangeSelector, formatTimeForParsing } from './FlexibleTimeRangeSelector'
import { ProviderCalendarSessionTypeSelector } from './ProviderCalendarSessionTypeSelector'
import { ProviderCalendarTimeSlotSelector } from './ProviderCalendarTimeSlotSelector'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { SelectField } from '../../atoms/formElements/SelectField'
import { PlusIcon } from '../../atoms/icons/PlusIcon'
import { TextButton } from '../../atoms/textButton/TextButton'
import { useLocaleDateFormat } from '../../hooks/useLocaleDateFormat'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { ProviderCalendarModalitySelector } from '../../molecules/providerCalendarModalitySelector/ProviderCalendarModalitySelector'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const TIME_FORMAT = 'hh:mm a'
const DEFAULT_SESSION_DURATION = 50
const DEFAULT_STEP_MINUTES = 30
const FLEXIBLE_TIME_FORMAT = 'h:mm a'

/** First day of the week (0=Sunday) that is not hidden, so the default selection is always one the provider can pick */
export const getDefaultDayOfWeek = (hiddenDays?: number[]): number =>
  [0, 1, 2, 3, 4, 5, 6].find((day) => !hiddenDays?.includes(day)) ?? 0

const TimeSelectorsTitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const TimeSlotContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
}))

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
  width: '100%',
}))

export type ProviderCalendarDaySlotSelectionContentProps = {
  addresses?: Address[] | null
  currentEvents?: { startTime: string; endTime: string; modality?: EventModality | null }[]
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  eventToEdit?: EventContentArg | null
  flexibleAvailabilityWindows?: boolean
  inPersonPreference?: boolean
  splitVideoOptions?: boolean
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  showModalitySelection?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  videoPreference?: boolean
  onValidityChange?: (valid: boolean) => void
  isNPSession?: boolean
  hiddenDays?: number[]
}

export type ProviderCalendarDaySlotSelectionContentRef = {
  buildPayload: () => BuiltEvent[]
  isValid: () => boolean
  reset: () => void
}

export const ProviderCalendarDaySlotSelectionContent = forwardRef<
  ProviderCalendarDaySlotSelectionContentRef,
  ProviderCalendarDaySlotSelectionContentProps
>(
  (
    {
      addresses,
      currentEvents = [],
      defaultVideoPlatform,
      eventToEdit,
      flexibleAvailabilityWindows = false,
      inPersonPreference,
      splitVideoOptions,
      rangeToAdd,
      showModalitySelection,
      slotMaxTime = '22:00:00',
      slotMinTime = '07:00:00',
      videoPreference,
      onValidityChange,
      isNPSession,
      hiddenDays,
    },
    ref,
  ) => {
    const { formatMessage } = useIntl()

    const formatTimeToLocale = (time: Date) =>
      new Intl.DateTimeFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(time)
    const localeDateFormat = useLocaleDateFormat()
    const hasAddresses = !!addresses && addresses?.length > 0
    // Determine if we should show the modality section to the provider, sanity check their modality options + flag
    const hasValidModalityConfiguration =
      showModalitySelection && (videoPreference || (inPersonPreference && hasAddresses))

    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<number>(() => getDefaultDayOfWeek(hiddenDays))
    const [selectedStartTimes, setSelectedStartTimes] = useState<string[]>([''])
    const [initialTime, setInitialTime] = useState<string | undefined>(undefined)
    const [selectedLocation, setSelectedLocation] = useState<Address | undefined>(
      hasAddresses ? addresses[0] : undefined,
    )
    const [inPersonModality, setInPersonModality] = useState<boolean>(!!inPersonPreference && hasAddresses)
    const [videoModality, setVideoModality] = useState<boolean>(!!videoPreference)
    const [selectedVideoPlatform, setSelectedVideoPlatform] = useState<VIDEO_PLATFORM>(
      defaultVideoPlatform ?? DEFAULT_VIDEO_PLATFORM,
    )

    // For flexible availability windows (Core Care)
    const [flexibleStartTime, setFlexibleStartTime] = useState<string>('')
    const [flexibleEndTime, setFlexibleEndTime] = useState<string>('')

    const startDate = parse(slotMinTime, 'HH:mm:ss', new Date())
    let endDate = parse(slotMaxTime, 'HH:mm:ss', new Date())
    if (isBefore(endDate, startDate)) {
      endDate = addDays(endDate, 1)
    }

    const { isCoreCareNPIntakeSessionEnabled } = useFlags()
    const showSessionTypeSelector = isNPSession || isCoreCareNPIntakeSessionEnabled
    const selectorMode = isNPSession ? SessionTypeSelectorMode.NP : SessionTypeSelectorMode.CORE_CARE

    const initialSessionType = useMemo(
      () =>
        getNpSessionTypeFromBookingType(eventToEdit?.event.extendedProps?.booking_type) ??
        DEFAULT_SESSION_TYPE_BY_MODE[selectorMode],
      [eventToEdit, selectorMode],
    )
    const [sessionType, setSessionType] = useState<NpSessionType | undefined>(
      showSessionTypeSelector ? initialSessionType : undefined,
    )
    const onChangeSessionType = (type: NpSessionType | undefined) => setSessionType(type)
    const sessionDuration = sessionType ? getNpSessionDuration(sessionType) : DEFAULT_SESSION_DURATION
    const isIntakeSelected = isCoreCareNPIntakeSessionEnabled && sessionType === NpSessionType.NP_CLINICAL_INTAKE
    const useFlexibleWindows = flexibleAvailabilityWindows && !isIntakeSelected

    const stepMinutes = isNPSession || isIntakeSelected ? 15 : DEFAULT_STEP_MINUTES

    useEffect(() => {
      if (!eventToEdit) return
      if (useFlexibleWindows) {
        const startTime = parse(eventToEdit.event.startStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
        const endTime = parse(eventToEdit.event.endStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
        setSelectedDayOfWeek(getDay(startTime))
        setFlexibleStartTime(formatTimeForParsing(startTime))
        setFlexibleEndTime(formatTimeForParsing(endTime))
      } else {
        const initialDate = parse(eventToEdit.event.startStr.replace('Z', ''), "yyyy-MM-dd'T'HH:mm:ss", new Date())
        setSelectedDayOfWeek(getDay(initialDate))
        setSelectedStartTimes([format(initialDate, TIME_FORMAT)])
        setInitialTime(format(initialDate, TIME_FORMAT))
      }
    }, [eventToEdit, useFlexibleWindows])

    useEffect(() => {
      if (showModalitySelection && eventToEdit?.event.extendedProps.modality_availability) {
        setVideoModality(eventToEdit?.event.extendedProps.modality_availability.video.available)
        setInPersonModality(eventToEdit?.event.extendedProps.modality_availability.in_person.available)
        if (eventToEdit?.event.extendedProps.modality_availability.in_person.location) {
          setSelectedLocation(eventToEdit?.event.extendedProps.modality_availability.in_person.location)
        }
        const platform = eventToEdit?.event.extendedProps.modality_availability.video.platform
        if (platform && Object.values(VIDEO_PLATFORM).includes(platform)) {
          setSelectedVideoPlatform(platform)
        }
      }
    }, [eventToEdit, showModalitySelection])

    const blockedRelativeStartTimes = useMemo(() => {
      const adjacent = Math.ceil(sessionDuration / 30) - 1
      const res: number[] = []
      for (let i = -adjacent; i <= adjacent; i++) res.push(i * stepMinutes)
      return res
    }, [sessionDuration, stepMinutes])
    const holdDurationMinutes = Math.ceil(sessionDuration / 30) * 30

    const timeSlots = useMemo(() => {
      const slots: { label: string; value: string }[] = []
      let currentTime = startDate
      while (isBefore(currentTime, subHours(endDate, 1)) || isEqual(currentTime, subHours(endDate, 1))) {
        slots.push({
          label: `${formatTimeToLocale(currentTime)} - ${formatTimeToLocale(
            addMinutes(currentTime, holdDurationMinutes),
          )}`,
          value: format(currentTime, TIME_FORMAT),
        })
        currentTime = addMinutes(currentTime, stepMinutes)
      }
      return slots
    }, [endDate, holdDurationMinutes, startDate, stepMinutes])

    const selectedDay = useMemo(() => addDays(startOfWeek(new Date()), Number(selectedDayOfWeek)), [selectedDayOfWeek])

    const selectedDayExistingEvents: { startTime: string; endTime: string }[] | undefined = useMemo(
      () =>
        selectedDay &&
        currentEvents
          .filter((evt) => format(new Date(evt.startTime), 'MM-dd-yy') === format(selectedDay, 'MM-dd-yy'))
          .filter(({ startTime }) => format(parseISO(startTime), TIME_FORMAT) !== initialTime)
          .map(({ startTime, endTime }) => ({
            startTime: format(parseISO(startTime), TIME_FORMAT),
            endTime: format(parseISO(endTime), TIME_FORMAT),
          })),
      [currentEvents, initialTime, selectedDay],
    )

    const blockedStartTimes = useMemo(
      () =>
        selectedDayExistingEvents
          ? [
              ...new Set(
                selectedDayExistingEvents
                  .map((e) => e.startTime)
                  .reduce((acc: string[], existing) => {
                    const blockedTimes = blockedRelativeStartTimes.map((offset) =>
                      format(addMinutes(parse(existing, TIME_FORMAT, new Date()), offset), TIME_FORMAT),
                    )
                    return acc.concat(blockedTimes)
                  }, []),
              ),
            ]
          : [],
      [blockedRelativeStartTimes, selectedDayExistingEvents],
    )

    const allAvailableTimeSlots = useMemo(
      () => timeSlots.filter((t) => !blockedStartTimes.includes(t.value)),
      [blockedStartTimes, timeSlots],
    )

    const blockedTimeSlotValues: { [key: string]: string[] } = useMemo(
      () =>
        selectedStartTimes.reduce((map: { [k: string]: string[] }, selected) => {
          if (isEmpty(selected)) return map
          blockedRelativeStartTimes.forEach((offset) => {
            const blocked = format(addMinutes(parse(selected, TIME_FORMAT, new Date()), offset), TIME_FORMAT)
            blocked in map ? map[blocked].push(selected) : (map[blocked] = [selected])
          })
          return map
        }, {}),
      [blockedRelativeStartTimes, selectedStartTimes],
    )

    const remainingAvailableTimeslots = useMemo(
      () => allAvailableTimeSlots.filter((t) => !Object.keys(blockedTimeSlotValues).includes(t.value)),
      [allAvailableTimeSlots, blockedTimeSlotValues],
    )

    const hasValidTimeSelections = useMemo(() => {
      if (useFlexibleWindows) {
        return !isEmpty(flexibleStartTime) && !isEmpty(flexibleEndTime)
      }
      return selectedStartTimes.filter((t) => !isEmpty(t)).length > 0
    }, [useFlexibleWindows, flexibleStartTime, flexibleEndTime, selectedStartTimes])

    const hasValidDay = !hiddenDays?.includes(selectedDayOfWeek)

    const hasValidModality = useMemo(() => {
      // If the provider doesn't have this feature enabled or lacks valid modality options, this should return true
      if (!hasValidModalityConfiguration) {
        return true
      }
      if (!!inPersonPreference && !!videoPreference) {
        return inPersonModality || videoModality
      }
      if (!!inPersonPreference) {
        return inPersonModality
      }
      return videoModality
    }, [hasValidModalityConfiguration, inPersonModality, inPersonPreference, videoModality, videoPreference])

    useEffect(() => {
      onValidityChange?.(hasValidTimeSelections && hasValidModality && hasValidDay)
    }, [hasValidDay, hasValidModality, hasValidTimeSelections, onValidityChange])

    useEffect(() => {
      if (rangeToAdd && !useFlexibleWindows) {
        const rangeStartTime = new Date(rangeToAdd.startLocal)
        let rangeEndTime = new Date(rangeToAdd.endLocal)
        let currentTime = rangeStartTime
        // startLocal/endLocal are "fake UTC"; see toFakeUTCISOString docs
        setSelectedDayOfWeek(rangeStartTime.getUTCDay())
        setInitialTime(formatInTimeZone(rangeStartTime, 'UTC', TIME_FORMAT))
        const initialSelectedTimeSlots: string[] = []
        if (isAfter(currentTime, subMinutes(rangeEndTime, holdDurationMinutes))) {
          rangeEndTime = addMinutes(rangeStartTime, holdDurationMinutes)
        }
        while (
          isBefore(currentTime, subMinutes(rangeEndTime, holdDurationMinutes)) ||
          isEqual(currentTime, subMinutes(rangeEndTime, holdDurationMinutes))
        ) {
          initialSelectedTimeSlots.push(formatInTimeZone(currentTime, 'UTC', TIME_FORMAT))
          currentTime = addMinutes(currentTime, holdDurationMinutes)
        }
        setSelectedStartTimes(initialSelectedTimeSlots.length > 0 ? initialSelectedTimeSlots : [''])
      }
    }, [
      useFlexibleWindows,
      holdDurationMinutes,
      localeDateFormat,
      rangeToAdd,
      sessionDuration,
      setSelectedStartTimes,
      sessionType,
    ])

    useEffect(() => {
      if (rangeToAdd && useFlexibleWindows) {
        const rangeStartTime = new Date(rangeToAdd.startLocal)
        const rangeEndTime = new Date(rangeToAdd.endLocal)
        // startLocal/endLocal are "fake UTC"; see toFakeUTCISOString docs
        setSelectedDayOfWeek(rangeStartTime.getUTCDay())
        setFlexibleStartTime(formatTimeForParsing(rangeStartTime, true))
        setFlexibleEndTime(formatTimeForParsing(rangeEndTime, true))
      }
    }, [useFlexibleWindows, rangeToAdd])

    const getNextTimeSlot = () => {
      const previousLatest = selectedStartTimes[selectedStartTimes.length - 1]
      if (isEmpty(previousLatest)) return ''
      const next = format(addMinutes(parse(previousLatest, TIME_FORMAT, new Date()), holdDurationMinutes), TIME_FORMAT)
      return remainingAvailableTimeslots.some((t) => t.value === next) && !selectedStartTimes.includes(next) ? next : ''
    }

    useImperativeHandle(
      ref,
      () => ({
        buildPayload: () => {
          const dateBase = addDays(startOfWeek(new Date()), Number(selectedDayOfWeek))
          const modality =
            showModalitySelection && (videoModality || (inPersonModality && !!selectedLocation))
              ? {
                  video: {
                    available: videoModality,
                    ...(splitVideoOptions && videoModality ? { platform: selectedVideoPlatform } : {}),
                  },
                  in_person: {
                    available: inPersonModality && !!selectedLocation,
                    location:
                      inPersonModality && !!selectedLocation
                        ? {
                            id: selectedLocation?.id,
                            city: selectedLocation?.city,
                            country: selectedLocation?.country,
                            state: selectedLocation?.state,
                            street1: selectedLocation?.street1,
                            street2: selectedLocation?.street2,
                            zipcode: selectedLocation?.zipcode,
                          }
                        : undefined,
                  },
                }
              : undefined

          if (useFlexibleWindows) {
            if (!flexibleStartTime || !flexibleEndTime) {
              return []
            }
            const startTime = parse(flexibleStartTime, FLEXIBLE_TIME_FORMAT, dateBase)
            let endTime = parse(flexibleEndTime, FLEXIBLE_TIME_FORMAT, dateBase)
            // Handle midnight (12:00 AM) - it parses to start of day, but should be end of day
            if (isBefore(endTime, startTime)) {
              endTime = addDays(endTime, 1)
            }
            // Output "fake UTC"; see toFakeUTCISOString docs
            return [
              {
                startTime: toFakeUTCISOString(startTime),
                endTime: toFakeUTCISOString(endTime),
                modality,
                // Omit booking_type when the selector is hidden; null clears a previous NP type in Core Care mode
                ...(showSessionTypeSelector && { booking_type: toBookingType(sessionType) }),
              },
            ]
          }

          return selectedStartTimes
            .filter((t) => !isEmpty(t))
            .map((selected) => {
              const startTime = parse(selected, TIME_FORMAT, dateBase)
              // Output "fake UTC"; see toFakeUTCISOString docs
              return {
                startTime: toFakeUTCISOString(startTime),
                endTime: toFakeUTCISOString(addMinutes(startTime, holdDurationMinutes)),
                modality,
                ...(showSessionTypeSelector && { booking_type: toBookingType(sessionType) }),
              }
            })
        },
        isValid: () => hasValidTimeSelections && hasValidModality && hasValidDay,
        reset: () => {
          setSelectedDayOfWeek(getDefaultDayOfWeek(hiddenDays))
          setSelectedStartTimes([''])
          setVideoModality(!!videoPreference)
          setInPersonModality(!!inPersonPreference && hasAddresses)
          setSelectedLocation(hasAddresses ? addresses[0] : undefined)
          setFlexibleStartTime('')
          setFlexibleEndTime('')
          setSelectedVideoPlatform(defaultVideoPlatform ?? DEFAULT_VIDEO_PLATFORM)
        },
      }),
      [
        addresses,
        defaultVideoPlatform,
        useFlexibleWindows,
        flexibleEndTime,
        flexibleStartTime,
        hasAddresses,
        hasValidDay,
        hasValidModality,
        hasValidTimeSelections,
        hiddenDays,
        holdDurationMinutes,
        inPersonModality,
        inPersonPreference,
        showSessionTypeSelector,
        isNPSession,
        splitVideoOptions,
        selectedDayOfWeek,
        selectedLocation,
        selectedStartTimes,
        sessionType,
        selectedVideoPlatform,
        showModalitySelection,
        videoModality,
        videoPreference,
      ],
    )

    const dayOfWeekOptions = useMemo(() => {
      const allDays = [
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Sunday), value: 0 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Monday), value: 1 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Tuesday), value: 2 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Wednesday), value: 3 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Thursday), value: 4 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Friday), value: 5 },
        { label: formatMessage(DAYS_OF_WEEK_LONG_LABELS.Saturday), value: 6 },
      ]
      if (hiddenDays && hiddenDays.length > 0) {
        return allDays.filter((option) => !hiddenDays.includes(option.value))
      }
      return allDays
    }, [formatMessage, hiddenDays])

    return (
      <Container>
        {!useFlexibleWindows && allAvailableTimeSlots.length === 0 && (
          <InlineWarningBanner
            testId={tID('ProviderCalendarBaseSlotSelectionModal-noAvailabilityBanner')}
            text={formatMessage({
              defaultMessage: 'No availability today. Choose another day',
              description:
                "Message to indicate that there are no timeslots that are bookable for the chosen day in the provider's calendar.",
            })}
          />
        )}

        <ProviderCalendarSessionTypeSelector
          show={showSessionTypeSelector}
          onChange={onChangeSessionType}
          initialValue={initialSessionType}
          mode={selectorMode}
        />

        <SelectField
          label={formatMessage({
            defaultMessage: 'Day of the week',
            description: 'Label for field where providers select the day of the week they are adding availability for',
          })}
          value={selectedDayOfWeek}
          name='selectedDayOfWeek'
          onChange={(e: number) => setSelectedDayOfWeek(e)}
          disablePlaceholder
          options={dayOfWeekOptions}
          baseInputStyle={{ marginBottom: 0 }}
        />

        {useFlexibleWindows ? (
          <FlexibleTimeRangeSelector
            slotMinTime={slotMinTime}
            slotMaxTime={slotMaxTime}
            selectedDate={selectedDay}
            selectedStartTime={flexibleStartTime}
            selectedEndTime={flexibleEndTime}
            onStartTimeChange={setFlexibleStartTime}
            onEndTimeChange={setFlexibleEndTime}
          />
        ) : (
          <TimeSlotContainer>
            <TimeSelectorsTitleContainer>
              <BodyText
                text={
                  sessionDuration != holdDurationMinutes ? (
                    <FormattedMessage
                      defaultMessage='Time ({sessionDuration}min sessions • {holdDurationHours} hour holds)'
                      description='Title for selectors where providers select times to mark as available for sessions'
                      values={{ sessionDuration, holdDurationHours: holdDurationMinutes / 60 }}
                    />
                  ) : (
                    <FormattedMessage defaultMessage='Time' description='Label for time slot selection section' />
                  )
                }
              />
            </TimeSelectorsTitleContainer>
            {selectedStartTimes.map((selectedStartTime, index) => (
              <ProviderCalendarTimeSlotSelector
                key={index}
                allOptions={allAvailableTimeSlots}
                blockedTimeSlotValues={blockedTimeSlotValues}
                index={index}
                selectedOption={selectedStartTime}
                selectedOptionList={selectedStartTimes}
                setSelectedOptionList={setSelectedStartTimes}
                showDeleteIcon={index !== 0}
                singleTimeSelector={selectedStartTimes.length === 1}
              />
            ))}
            {isNil(eventToEdit) && remainingAvailableTimeslots.length > 0 && (
              <TextButton
                text={
                  <FormattedMessage
                    defaultMessage='Add another time'
                    description='Button label for provider to add another time slot selection'
                  />
                }
                leftIcon={<PlusIcon />}
                onPress={() => setSelectedStartTimes((prev) => [...prev, getNextTimeSlot()])}
                testID={tID('ProviderCalenderSlotSelectionModal-addSlotButton')}
              />
            )}
          </TimeSlotContainer>
        )}
        {hasValidModalityConfiguration && (
          <ProviderCalendarModalitySelector
            addresses={addresses}
            inPersonModality={inPersonModality}
            inPersonPreference={inPersonPreference}
            splitVideoOptions={splitVideoOptions}
            selectedLocation={selectedLocation}
            selectedVideoPlatform={selectedVideoPlatform}
            setInPersonModality={setInPersonModality}
            setSelectedLocation={setSelectedLocation}
            setSelectedVideoPlatform={setSelectedVideoPlatform}
            setVideoModality={setVideoModality}
            videoModality={videoModality}
            videoPreference={videoPreference}
          />
        )}
      </Container>
    )
  },
)
